import * as XLSX from 'xlsx';
import { useState } from "react";
import { format } from 'date-fns';
import { gql, useLazyQuery } from "@apollo/client";
import styles from "./index.module.scss";
import { Heading, Text } from "../../../Type";
import TextLink from "../../../TextLinks";
import { useClientFileContext } from "../../../../contexts/ClientFileProvider";
import GeneratePdfModal from "../../../ADP/GeneratePdfModal";
import Modal from "../../../Modal";

const GET_FILE_EVENTS = gql`
  query FileEvents(
    $sort: String
    $where: JSON
  ) {
    fileEvents(sort: $sort, where: $where) {
      message
      created_at
    }
  }
`;

const SubmissionSummary = () => {
  const { clientFile } = useClientFileContext();
  const [, setBoundaryError] = useState<Error | null>(null);
  const name = `${clientFile.client.first_name} ${clientFile.client.last_name}`;
  const cancelled = clientFile.status === "Cancelled";
  const submitted = clientFile.status === "Submitted";
  const rejected = clientFile.status === "Rejected";
  const approved = clientFile.status === "Approved";
  const dispensed = clientFile.status === "Dispensed";
  const withdrawn = clientFile.status === "Withdrawn";

  const headingContent = cancelled ?
  "Application cancelled after completion"
  : submitted ? "Application submitted to ADP"
  : rejected ? "Application rejected by ADP"
  : approved ? "Application approved by ADP"
  : dispensed ? "Application approved by ADP"
  : withdrawn ? "Application withdrawn by ADP"
  :"Application ready to be submitted to ADP";

  const subheadingContent = cancelled ? `${name}’s application was completed and then cancelled.`
  : submitted ? `${name}’s application was exported and sent to ADP.`
  : rejected ? `${name}’s application was reviewed and rejected by ADP.`
  : approved ? `${name}’s application was reviewed and approved by ADP.`
  : dispensed ? `${name}’s application was reviewed and approved by ADP. The device has been sent to the client. `
  : withdrawn ? `${name}’s application was reviewed and withdrawn by ADP. `
  : `${name}’s application is ready to be exported and sent to ADP.`;


  const filesToDisplay = [
    ["client_signature", "Client Signature"],
    ["quote", "Vendor Quote"],
    ["justification", "Justification of Funding Chart"],
    ["letter_of_rationale", "Letter of Rationale"],
    ["confirmation_of_benefits", "Confirmation of Benefits"],
    ["wet_signature", "Wet Signature Document"],
    ["other_supporting_documents", "Other Supporting Documents"],
    ["other_supporting_documents_alt", "Other Supporting Documents (Alt)"]
  ];

  const [fileEvent] = useLazyQuery(GET_FILE_EVENTS, {
    onCompleted: (data) => {
      // insert client file information
      const name = `Name: ${clientFile.client.first_name} ${clientFile.client.last_name}\r\n`;
      const health_card = `Health Card: ${clientFile.client.health_card}\r\n`;
      const status = `Status: ${clientFile.status}\r\n\r\n`;

      // skip original headers and add new one
      const headers = [["Action", "Date and Time"]];
      const sheet = XLSX.utils.json_to_sheet(data.fileEvents, { skipHeader: true });
      XLSX.utils.sheet_add_aoa(sheet, headers);

      // convert sheet to csv
      const csv = XLSX.utils.sheet_to_csv(sheet);
      const plain = new Blob([name, health_card, status, csv], { type: 'text/plain' });

      // create a link and download
      const element = document.createElement("a");
      element.href = URL.createObjectURL(plain);
      const now = format(new Date, "yyyyMMddHHmmSS")
      element.download = `audit-report-file-${clientFile.id}-${now}.csv`;
      document.body.appendChild(element);
      element.click();
    },
    onError: (error) => {
      setBoundaryError(() => {
        throw error;
      });
    },
  });

  const handleReport = () => {
    fileEvent({
      variables: {
        where: {
          client_file: clientFile.id
        },
        sort: "created_at:ASC",
      },
    });
   };

  function renderFileRow(filename) {
    const databaseName = filename[0];
    const displayName = filename[1];

    return (
      <tr key={databaseName}>
        <td>
          <Text weight="bold" className={styles.divMB}>
            {displayName}
          </Text>
        </td>
        <td className={styles.fileContainer}>
          {clientFile[databaseName]?.name ? (
            <TextLink
              className={styles.divMB}
              to={`${clientFile[databaseName]?.url}`}
              externalURL
            >
              {clientFile[databaseName]?.name}
            </TextLink>
          ) : (
            <Text className={styles.divMB}>File not uploaded</Text>
          )}
        </td>
      </tr>
    );
  }

  const [openGenerateModal, setOpenGenerateModal] = useState(false);
  const handleGenerate = () => {
    setOpenGenerateModal(false);
  };

  return (
    <div>
      <div className={styles.whiteBox}>
        <Heading className={styles.topHeading} size="h3">
          {headingContent}
        </Heading>
        <Text className={styles.txtStyle}>
          {subheadingContent}
        </Text>
        { !cancelled &&
          <Text className={styles.txtStyle}>
            Please download a copy for your records.
          </Text>
        }
        <div className={styles.topDiv}>
          <Heading size="h5" className={styles.headingMB}>
            LATEST COMPLETED ADP FORM
          </Heading>
          { !cancelled &&
            <TextLink
              className={styles.topDivMB}
              to={`${process.env.REACT_APP_API_URL}/client-file/${clientFile.id}/xml`}
              externalURL
            >
            {`${clientFile.client.first_name?.slice(0,1).toLowerCase()}${clientFile.client.last_name?.toLowerCase()}_applicationform.xml`}
            </TextLink>
          }
          {clientFile.ds_signed_form?.name ? (
            <TextLink
              className={styles.topDivMB}
              to={`${clientFile.ds_signed_form?.url}`}
              externalURL
            >
              {`${clientFile.client.first_name?.slice(0,1).toLowerCase()}${clientFile.client.last_name?.toLowerCase()}_applicationform.pdf`}
            </TextLink>
          ) : (
            <TextLink
              onClick={() => setOpenGenerateModal(true)}
            >
              Generate Application PDF
            </TextLink>
          )}
          {clientFile.ds_signed_form_original?.name && (
          <div className={styles.originalDiv}>
            <Heading size="h5" className={styles.headingMB}>
              PREVIOUS COMPLETED ADP FORM
            </Heading>
            <TextLink
              className={styles.topDivMB}
              to={`${clientFile.ds_signed_form_original?.url}`}
              externalURL
            >
              {`${clientFile.client.first_name?.slice(0,1).toLowerCase()}${clientFile.client.last_name?.toLowerCase()}_applicationform_original.pdf`}
            </TextLink>
          </div>
          )}
        </div>
      </div>
      <div className={styles.middleDiv}>
        <Heading size="h5" className={styles.headingMB}>
          SUPPORTING DOCUMENTS
        </Heading>
        <table className={styles.table}>
          <tbody>{filesToDisplay.map(renderFileRow)}</tbody>
        </table>
      </div>
      <div className={styles.bottomDiv}>
        <Heading size="h5" className={styles.headingMB}>
          APPLICATION HISTORY
        </Heading>
        <table className={styles.table}>
          <tr>
            <td>
              <Text weight="bold" className={styles.divMB}>
                Application Change History
              </Text>
            </td>
            <td className={styles.td}>
              <label id="audit-report" htmlFor="report">
                <Text size="medium" className={styles.reportText}>
                  <span>Report.csv</span>
                </Text>
                <input
                  type="button"
                  id="report"
                  className={styles.hiddenInput}
                  onClick={handleReport}
                />
              </label>
            </td>
          </tr>
        </table>
      </div>
      <div className={styles.btnStyle}>
        <TextLink to="/dashboard">Return to dashboard</TextLink>
      </div>
      <Modal
        isOpen={openGenerateModal}
        title="Generate PDF"
        onClose={handleGenerate}
        closeOnOverlay={false}
        >
        <GeneratePdfModal
          clientFormId={clientFile?.client_form.id}
          setGenerateModalOpen={handleGenerate}
        />
      </Modal>
    </div>
  );
};

export default SubmissionSummary;
